/**
 * ⌘F / ⌘K workspace search overlay. Searches every navigable entity —
 * nested modules, interfaces, surfaces, processes, subprocesses, types and
 * views — by name, type keyword and qualified path (see `searchIndex`), and
 * routes the picked result to its place via the shell's `onPick` dispatcher.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  rankEntries,
  type SearchEntry,
  type SearchKind,
} from "../search/searchIndex";
import { useContextMenu, type ContextMenuItem } from "@archlang/ui";
import { displayLabel } from "../displayLabel.js";
import { useTranslation } from "../i18n";

interface SearchModalProps {
  readonly open: boolean;
  readonly entries: readonly SearchEntry[];
  readonly onClose: () => void;
  readonly onPick: (entry: SearchEntry) => void;
}

const KIND_GLYPH: Record<SearchKind, string> = {
  module: "▣",
  interface: "⟶",
  surface: "◳",
  process: "⤳",
  subprocess: "↳",
  type: "◆",
  plane: "⧉",
};

const KIND_LABEL: Record<SearchKind, string> = {
  module: "module",
  interface: "interface",
  surface: "surface",
  process: "process",
  subprocess: "subprocess",
  type: "type",
  plane: "plane",
};

export function SearchModal({ open, entries, onClose, onPick }: SearchModalProps) {
  const { t } = useTranslation("search");
  const menu = useContextMenu();
  const [query, setQuery] = useState("");
  const [index, setIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const hits = useMemo(() => rankEntries(entries, query), [entries, query]);

  // Reset highlight when the query or open state changes.
  useEffect(() => {
    setIndex(0);
  }, [query, open]);

  // Clamp the highlight when the result set shrinks WITHOUT a query change —
  // the entries prop can change while the modal stays open (the editable
  // viewer recompiles the model on every edit; the active package/file can
  // switch). A stale index past the end leaves no row highlighted and turns
  // Enter into a silent no-op (`hits[staleIndex]` is undefined).
  useEffect(() => {
    setIndex((i) => Math.min(i, Math.max(0, hits.length - 1)));
  }, [hits.length]);

  // Focus the input on open.
  useEffect(() => {
    if (open) {
      setQuery("");
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  if (!open) return null;

  const pick = (entry: SearchEntry) => {
    onPick(entry);
    onClose();
  };

  const itemsFor = (hit: SearchEntry): ContextMenuItem[] => [
    { label: t("menu.open"), onClick: () => pick(hit) },
    {
      label: t("menu.copyName"),
      separator: true,
      onClick: () => void navigator.clipboard?.writeText(displayLabel(hit.label)),
    },
    ...(hit.path
      ? [
          {
            label: t("menu.copyPath"),
            onClick: () => void navigator.clipboard?.writeText(hit.path),
          },
        ]
      : []),
  ];

  return (
    <div
      className="fixed inset-0 z-overlay flex items-start justify-center pt-[16vh] bg-slate-950/(--tint-dim) backdrop-blur-xs"
      onClick={onClose}
      data-hud
      data-search-modal
    >
      <div
        className="w-[560px] max-w-[90vw] rounded-xl border border-slate-700 bg-slate-900 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-700">
          <span className="text-slate-500 text-sm" aria-hidden>
            ⌘F
          </span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("searchPlaceholder")}
            className="flex-1 bg-transparent text-slate-100 placeholder:text-slate-500 text-base outline-hidden"
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setIndex((i) => Math.min(hits.length - 1, i + 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setIndex((i) => Math.max(0, i - 1));
              } else if (e.key === "Enter") {
                e.preventDefault();
                const hit = hits[index];
                if (hit) pick(hit);
              } else if (e.key === "Escape") {
                e.preventDefault();
                onClose();
              }
            }}
          />
        </div>
        <ul className="max-h-[55vh] overflow-y-auto">
          {hits.length === 0 && (
            <li className="px-4 py-8 text-center text-slate-500 text-sm">
              {t("noResults")}
            </li>
          )}
          {hits.map((hit, i) => {
            const active = i === index;
            return (
              <li
                key={hit.id}
                onMouseEnter={() => setIndex(i)}
                onClick={() => pick(hit)}
                onContextMenu={(e) => menu.open(e, itemsFor(hit))}
                className={
                  "px-4 py-2 cursor-pointer flex flex-col gap-0.5 " +
                  (active
                    ? "bg-sky-500/20 text-white"
                    : "text-slate-200 hover:bg-slate-800")
                }
              >
                <div className="flex items-baseline gap-2">
                  <span
                    className="text-slate-500 w-4 text-center shrink-0"
                    title={KIND_LABEL[hit.kind]}
                    aria-hidden
                  >
                    {KIND_GLYPH[hit.kind]}
                  </span>
                  <span className="font-medium truncate">{displayLabel(hit.label)}</span>
                  {hit.typeName && (
                    <span className="text-[11px] text-slate-500 shrink-0">
                      {hit.typeName}
                    </span>
                  )}
                  <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-600 shrink-0">
                    {KIND_LABEL[hit.kind]}
                  </span>
                </div>
                {hit.path && (
                  <div className="text-xs text-slate-500 truncate pl-6">
                    {hit.path.split(".").map(displayLabel).join(" › ")}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        <div className="px-4 py-2 border-t border-slate-700 text-[11px] text-slate-500 flex gap-4">
          <span>
            <kbd className="font-mono text-slate-300">↑↓</kbd> {t("navigate")}
          </span>
          <span>
            <kbd className="font-mono text-slate-300">↵</kbd> {t("jump")}
          </span>
          <span>
            <kbd className="font-mono text-slate-300">esc</kbd> {t("close")}
          </span>
        </div>
      </div>
      {menu.node}
    </div>
  );
}
